/**
 * SF-4651: sf-api calls for the demo wizard. Same pattern as src/pages/eva/api/*: thin hooks over
 * Refine's useCustom / useCustomMutation, whose data provider unwraps the sf-api `{ success, data }`
 * envelope, so the inner payload is `query.data?.data` / `result.data`.
 *
 *   GET  /admin/demo-builds?scope&created_by&status&include_dismissed&limit&offset
 *                                            my builds, or everyone's with read_all (SF-4746)
 *   POST /admin/demo-builds                  start a build      (SF-4648)
 *   GET  /admin/demo-builds/:id              one build          (SF-4648)
 *   POST /admin/demo-builds/:id/transcripts  multipart `files`  (SF-4649)
 *   POST /admin/demo-builds/:id/draft        Eva drafts now     (SF-4649, after the transcripts)
 *   POST /location/geocodeArea {text}        town / postcode    (SF-4643, sf-api #4075)
 *   POST /admin/demo-builds/:id/dismiss      leaves my list, the record is kept (SF-4746)
 *   GET  /admin/demo-builds/module-options   the wizard's module list (SF-4847)
 * The demo-builds writes need admin:demos:create and only act on the caller's own builds; the
 * list, GET /:id and GET /:id/messages also take admin:demos:read_all (SF-4746). geocodeArea
 * needs a super admin.
 */
import { useCallback, useMemo } from 'react';
import { useCustom, useCustomMutation } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import type { CheckedArea } from '../validation';

export const DEMO_BUILDS_BASE = 'admin/demo-builds';
export const GEOCODE_AREA_URL = '/location/geocodeArea';

export type DemoBuildStatus =
  | 'awaiting_transcripts'
  | 'drafting'
  | 'ready_for_review'
  | 'building'
  | 'done'
  | 'failed'
  | 'cancelled';

/** Who started a build (SF-4746). sf-api may not know the name. */
export interface DemoBuildCreator {
  id: string;
  name: string | null;
  email: string | null;
}

/** One row of GET /admin/demo-builds (sf-api toListItem). */
export interface DemoBuildListItem {
  id: string;
  status: DemoBuildStatus;
  environment: string;
  domain: string | null;
  area_label: string | null;
  change_set_version: number;
  error: string | null;
  created_at: string | null;
  updated_at: string | null;
  created_by: DemoBuildCreator;
  dismissed_at: string | null;
  is_mine: boolean;
}

export interface DemoBuildList {
  items: DemoBuildListItem[];
  total: number;
  limit: number;
  offset: number;
}

/** GET /admin/demo-builds/:id and the POST answer (sf-api toView; fields this page reads). */
export interface DemoBuild {
  id: string;
  status: DemoBuildStatus;
  environment: string;
  inputs: {
    domain: string;
    area: CheckedArea;
    notes?: string;
    transcript_ids?: string[];
    /** SF-4847: the wizard's Modules step. Absent on older builds, which means eva_decides. */
    modules?: DemoModulesInput;
    /** SF-4847: every remove_id the person has taken out so far (cumulative, kept by sf-api). */
    removed?: string[];
  };
  error: string | null;
  /**
   * Why it failed, as a code (SF-4728): DRAFT_TIMED_OUT when Eva's draft ran out of time.
   * SF-5050: DRAFT_EXPIRED when Eva no longer holds the draft (approve, carry on or a chat edit
   * came too late). Carry on can never work then; `error` says to draft it again.
   */
  error_code?: string | null;
  created_at: string | null;
  updated_at: string | null;
  created_by: DemoBuildCreator;
  /** False for someone else's build (read with admin:demos:read_all): the page is read only. */
  is_mine: boolean;
  dismissed_at: string | null;
  /** sf-api decides both (false for someone else's build); the pages only read them. */
  can_draft_again: boolean;
  can_dismiss: boolean;
}

export interface DemoBuildCreate {
  domain: string;
  area: CheckedArea;
  notes?: string;
  /** Files will be sent next, so sf-api waits for POST .../draft before Eva drafts. */
  transcripts_to_follow?: boolean;
  /** SF-4847: stored by sf-api as inputs.modules. */
  modules?: DemoModulesInput;
}

/** SF-4847: the wizard's Modules choice (sf-api validates the shape, chat-service the keys). */
export type DemoModulesInput = { mode: 'eva_decides' } | { mode: 'chosen'; keys: string[] };

/** SF-4847: one module the wizard offers (sf-chat-service DEMO_MODULE_OPTIONS). */
export interface DemoModuleOption {
  key: string;
  name: string;
  /** What fills it with demo data; null for Eva, which has none of its own. */
  data: string | null;
  default_on: boolean;
}

/** SF-4847: GET /admin/demo-builds/module-options. `base` modules are on in every demo. */
export interface DemoModuleOptions {
  options: DemoModuleOption[];
  base: Array<{ key: string; name: string }>;
}

/**
 * SF-4847: the modules the wizard can offer, read from chat-service through sf-api (503
 * MODULE_OPTIONS_UNAVAILABLE when it cannot answer). Not retried: the wizard then offers only
 * "Let Eva decide", which needs no list.
 */
export function useDemoModuleOptions(enabled: boolean) {
  return useCustom<DemoModuleOptions>({
    url: `/${DEMO_BUILDS_BASE}/module-options`,
    method: 'get',
    queryOptions: {
      queryKey: ['sf-admin', 'demo-module-options'],
      enabled,
      retry: false,
      refetchOnWindowFocus: false,
    },
    errorNotification: false,
  });
}

/** sf-api's answer to POST .../draft when Eva has already been asked (a repeat call). */
export const DEMO_BUILD_NOT_AWAITING = 'DEMO_BUILD_NOT_AWAITING';
/** POST .../draft on a failed build whose call files are no longer held (SF-4746). */
export const DEMO_BUILD_CANNOT_DRAFT_AGAIN = 'DEMO_BUILD_CANNOT_DRAFT_AGAIN';
/** POST .../dismiss on a build that is not failed or cancelled before anything was built. */
export const DEMO_BUILD_NOT_DISMISSABLE = 'DEMO_BUILD_NOT_DISMISSABLE';
/** SF-5050: a build's `error_code` when Eva no longer holds its draft (see DemoBuild.error_code). */
export const DRAFT_EXPIRED = 'DRAFT_EXPIRED';

export interface TranscriptResult {
  id: string;
  name: string;
  chars: number;
  status: 'read' | 'refused';
  reason?: string;
}

export interface GeocodedArea {
  label: string;
  lat: number;
  lng: number;
}

/** GET /admin/demo-builds query. `created_by` and `status` only count with scope `all`. */
export interface DemoBuildsQuery {
  scope: 'mine' | 'all';
  include_dismissed: boolean;
  created_by?: string;
  status?: DemoBuildStatus;
  limit: number;
  offset: number;
}

/**
 * The React Query key prefix of every list read (each scope, filter and page). Fixed here so a
 * dismiss can drop every cached list page at once (SF-4746).
 */
export const demoBuildsListKeyPrefix = ['sf-admin', 'demo-builds'] as const;

/** My demo builds, or everyone's (scope `all`), newest first. */
export function useDemoBuilds(query: DemoBuildsQuery, enabled: boolean) {
  return useCustom<DemoBuildList>({
    url: `/${DEMO_BUILDS_BASE}`,
    method: 'get',
    config: { query },
    queryOptions: {
      queryKey: [...demoBuildsListKeyPrefix, query],
      enabled,
      keepPreviousData: true,
    },
    errorNotification: false,
  });
}

/**
 * The React Query key of one build's detail read. Fixed here (not Refine's generated key) so a
 * page can put the build a POST answered with straight into the cache (SF-4652). The detail, review and progress pages all read the build under it (SF-4653).
 */
export const demoBuildQueryKey = (id: string | undefined) => ['sf-admin', 'demo-build', id ?? ''];

/**
 * One demo build (the creator's, or anyone's with admin:demos:read_all; otherwise a 404). `refetchInterval` re-reads it; as a
 * function it is asked after every read with the cached build, so polling follows the cached
 * status even when a read fails (SF-4652).
 */
export function useDemoBuild<T extends DemoBuild = DemoBuild>(
  id: string | undefined,
  enabled: boolean,
  refetchInterval: number | false | ((build: T | undefined) => number | false) = false,
) {
  return useCustom<T>({
    url: `/${DEMO_BUILDS_BASE}/${encodeURIComponent(id ?? '')}`,
    method: 'get',
    queryOptions: {
      queryKey: demoBuildQueryKey(id),
      enabled: enabled && !!id,
      keepPreviousData: false,
      refetchInterval:
        typeof refetchInterval === 'function'
          ? (data) => refetchInterval(data?.data)
          : refetchInterval,
      refetchIntervalInBackground: false,
    },
    errorNotification: false,
  });
}

/** Every wizard write (create, transcripts, geocode) goes through this one mutation hook. */
export const useDemoMutation = useCustomMutation;

const quiet = { errorNotification: false as const, successNotification: false as const };

export const createDemoBuildArgs = (values: DemoBuildCreate) => ({
  url: `/${DEMO_BUILDS_BASE}`,
  method: 'post' as const,
  values,
  ...quiet,
});

/**
 * The files as multipart field `files`. The shared axios client defaults to a JSON content type,
 * which would make axios turn the form into JSON. `false` tells axios to send no Content-Type at
 * all (not even its own default), so the browser writes `multipart/form-data; boundary=...`. A
 * fixed `multipart/form-data` would have no boundary and sf-api could not read the files.
 */
export function uploadTranscriptsArgs(buildId: string, files: File[]) {
  const form = new FormData();
  files.forEach((f) => form.append('files', f, f.name));
  return {
    url: `/${DEMO_BUILDS_BASE}/${encodeURIComponent(buildId)}/transcripts`,
    method: 'post' as const,
    values: form,
    config: { headers: { 'Content-Type': false } },
    ...quiet,
  };
}

/** Eva drafts the build now, with whatever transcripts were read; or again, after a failed draft. */
export const startDraftArgs = (buildId: string) => ({
  url: `/${DEMO_BUILDS_BASE}/${encodeURIComponent(buildId)}/draft`,
  method: 'post' as const,
  values: {},
  ...quiet,
});

export const geocodeAreaArgs = (text: string) => ({
  url: GEOCODE_AREA_URL,
  method: 'post' as const,
  values: { text },
  ...quiet,
});

/** The sf-api `code` of a failed call, if any. */
export function demoErrorCode(error: unknown): string | undefined {
  return (error as AxiosError<{ code?: string }>)?.response?.data?.code;
}

/** The sf-api message for a failed call, with plain words for the common statuses. */
/** The HTTP status of a failed demo builds call, if it had one. */
export function demoErrorStatus(error: unknown): number | undefined {
  const err = error as AxiosError & { statusCode?: number };
  return err?.response?.status ?? err?.statusCode;
}

/** A read that says the build is gone or no longer this person's (not a passing blip). */
export const isDemoBuildGone = (error: unknown): boolean => {
  const status = demoErrorStatus(error);
  return status === 403 || status === 404;
};

export function demoErrorMessage(error: unknown, fallback: string): string {
  const err = error as AxiosError<{ message?: string }> & { statusCode?: number };
  const status = err?.response?.status ?? err?.statusCode;
  const serverMessage = err?.response?.data?.message;
  if (status === 403) return serverMessage ?? 'You do not have permission to create demos.';
  return serverMessage ?? fallback;
}

/*
 * SF-4652: the review page. sf-api routes (routes/admin/demoBuilds.js):
 *   POST /admin/demo-builds/:id/messages {text, client_id}   ask Eva for a change (ready_for_review -> drafting)
 *   GET  /admin/demo-builds/:id/messages          the side chat so far, from Eva (sf-api #4089)
 *   POST /admin/demo-builds/:id/approve           {change_set_version, change_set_hash, confirm_company_name?}
 *   POST /admin/demo-builds/:id/cancel            awaiting_transcripts | drafting | ready_for_review
 * The page reads the chat once on open and re-reads GET /:id while Eva is drafting.
 */

/** One change in a group, as Eva's change set view has it (sf-chat-service demo-builder.view.ts). */
export interface ChangeSetItem {
  ref: string;
  summary: string;
  /** For a survey: the prospect's problem it answers. */
  problem?: string;
  /** Colours and logo for the company; each question for a survey. */
  details?: string[];
  /** For a demo location: its map point, for a pin on the review map. */
  location?: { lat: number; lng: number };
  /**
   * SF-4847: set on items the person may take out ("site:<place id>", "user:<email>"), stable
   * across versions. Absent or null: not removable.
   */
  remove_id?: string | null;
}

/** SF-4847: one module row of a change set (sf-chat-service demo-builder.view.ts `modules`). */
export interface ChangeSetModule {
  key: string;
  name: string;
  /** Whether the demo company will have it switched on. */
  on: boolean;
  /** What fills it with demo data, or null. */
  data: string | null;
  /** On in every demo; never removable. */
  base: boolean;
  /** "module:<key>" when it may be taken out; null for base and off rows. */
  remove_id: string | null;
}

export interface ChangeSetGroup {
  group: string;
  count: number;
  items: ChangeSetItem[];
}

/** SF-4856: one site to show in the demo (sf-chat-service `<demo_plan>` sites). */
export interface DemoSite {
  ref: string;
  role: 'best' | 'struggling';
  problem?: string;
  /** The location step's name, when sf-chat-service sends it. */
  name?: string;
  /** The region tag the site is in, when the set makes region tags. */
  region?: string;
}

/** `change_set` on a demo build: what approve builds. */
export interface DemoChangeSet {
  id: string;
  version: number;
  hash: string;
  /** The name production asks the person to type before approving. */
  company_name?: string;
  groups: ChangeSetGroup[];
  warnings?: string[];
  /** The prospect's problems in their own words; SF-4849 adds a plain-words detail to each. */
  problems?: Array<{ text: string; detail?: string; source?: string }>;
  /** SF-4849: other things learned from the calls and notes. Absent on older sets. */
  learnings?: Array<{ text: string; source?: string }>;
  /**
   * SF-4856: the sites to show in the demo. `ref` is a location item's ref in `groups`; `problem`
   * is one of `problems` (empty for the best site). Absent on older sets.
   */
  sites?: DemoSite[];
  /** What Eva says about this version. */
  message?: string;
  /** SF-4847: one row per module. Absent on older sets, which then render as before. */
  modules?: ChangeSetModule[];
}

export interface DemoBuildWithChangeSet extends DemoBuild {
  change_set: DemoChangeSet | null;
  change_set_version: number;
}

/** sf-api error codes the review page answers in its own words. */
export const CHANGE_SET_STALE = 'CHANGE_SET_STALE';
export const CONFIRM_NAME_MISMATCH = 'CONFIRM_NAME_MISMATCH';
export const DEMO_BUILD_BUSY = 'DEMO_BUILD_BUSY';
/** SF-4847 S6: approve refused, the plan still has items the person took out. */
export const PLAN_HAS_REMOVED_ITEMS = 'PLAN_HAS_REMOVED_ITEMS';
export const PLAN_HAS_REMOVED_ITEMS_TEXT =
  "Eva's last version still had items you took out. Make a new version.";
/** sf-api's words for CHANGE_SET_STALE on a message, used when its answer has none. */
export const CHANGE_SET_STALE_TEXT =
  'The plan changed while you were editing it. Reload and try again.';

const buildAction = (buildId: string, action: string, values: object) => ({
  url: `/${DEMO_BUILDS_BASE}/${encodeURIComponent(buildId)}/${action}`,
  method: 'post' as const,
  values,
  ...quiet,
});

/** One line of the side chat as sf-api gives it back: the person's words or Eva's, oldest first. */
export interface DemoBuildMessage {
  /** The server's id for the line; lines saved before ids existed have none. */
  id?: string;
  from: 'you' | 'eva';
  text: string;
  at: string;
  /** A 'you' line: the client_id the page sent with it. */
  client_id?: string;
  /** An 'eva' line: the change set version that turn left on screen. */
  version?: number;
  /** An 'eva' line: the client_id of the message it answers. */
  reply_to?: string;
}

/** The chat as one read found it. */
export interface DemoBuildHistory {
  messages: DemoBuildMessage[];
}

/**
 * The build's side chat as sf-api holds it: the page's source of truth for what was said. Read each
 * time the review page opens (a cached copy would miss lines added since), and again when the page
 * asks (`reread`); never on window focus or reconnect. A failed read is tried once more, then left
 * to the page, which keeps the last chat it read and offers to retry.
 */
export function useDemoBuildMessages(id: string | undefined, enabled: boolean) {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['sf-admin', 'demo-build-messages', id ?? ''], [id]);
  const query = useCustom<DemoBuildHistory>({
    url: `/${DEMO_BUILDS_BASE}/${encodeURIComponent(id ?? '')}/messages`,
    method: 'get',
    queryOptions: {
      queryKey,
      enabled: enabled && !!id,
      retry: 1,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
    errorNotification: false,
  });
  const { refetch } = query;
  /**
   * Read again, dropping any read under way so an older one never lands after it. refetch alone
   * cancels only once the chat has been read; before that it joins the read under way (query-core
   * Query.fetch), which may have started before what this re-read is for.
   */
  const reread = useCallback(() => {
    void queryClient.cancelQueries({ queryKey, exact: true });
    return refetch();
  }, [queryClient, queryKey, refetch]);
  return { ...query, reread };
}

/** `clientId` is the page's uuid for this send: sf-api saves it on the line, so the page knows it. */
export const sendMessageArgs = (buildId: string, text: string, clientId: string) =>
  buildAction(buildId, 'messages', { text, client_id: clientId });

/**
 * SF-4847: "Make version N": the items taken out (remove_ids) and any words typed in the chat
 * box. `text` may be empty here only (sf-api then sends Eva its own wording). The version is the
 * one the removals were picked from.
 */
export const sendRemovalsArgs = (
  buildId: string,
  body: { text: string; clientId: string; version: number; removed: string[] },
) =>
  buildAction(buildId, 'messages', {
    text: body.text,
    client_id: body.clientId,
    change_set_version: body.version,
    removed: body.removed,
  });

export const approveArgs = (
  buildId: string,
  /** `change_set_hash` is `change_set.hash` of the set on screen: a version number alone can name a
   * different plan after Draft again starts a new Eva session (sf-api 409 CHANGE_SET_STALE). */
  body: { change_set_version: number; change_set_hash: string; confirm_company_name?: string },
) => buildAction(buildId, 'approve', body);

export const cancelArgs = (buildId: string) => buildAction(buildId, 'cancel', {});

/** SF-4746: the build leaves the creator's list; sf-api keeps the record. */
export const dismissArgs = (buildId: string) => buildAction(buildId, 'dismiss', {});

// ---------------------------------------------------------------------------
// SF-4653: the run (progress and results). sf-api toView fields of an approved build.
//   POST /admin/demo-builds/:id/resume      carry on a failed run (failed -> building)
//   GET  /admin/demo-builds/:id/demo-login  the demo login, on every View (SF-4713; 404
//                                           DEMO_LOGIN_NOT_READY until it exists)
// ---------------------------------------------------------------------------

/** How often the progress page reads a build while it is building. */
export const DEMO_PROGRESS_POLL_MS = 3000;

/** sf-api's 404 code while the demo company's shared password or the login email is missing. */
export const DEMO_LOGIN_NOT_READY = 'DEMO_LOGIN_NOT_READY';

export type DemoBuildStepStatus = 'waiting' | 'running' | 'done' | 'failed';

/** One change of the run. `error` is the chat service's fixed message for a failed step. */
export interface DemoBuildStep {
  ref: string;
  group?: string | null;
  summary?: string | null;
  status: DemoBuildStepStatus;
  error?: string | null;
  created_id?: string | null;
}

export interface DemoBuildCheck {
  label: string;
  passed: boolean;
  detail?: string | null;
}

export interface DemoBuildRunView extends DemoBuild {
  steps?: DemoBuildStep[];
  checks?: DemoBuildCheck[];
  created?: {
    company_id?: string | null;
    location_ids?: string[];
    user_ids?: string[];
    survey_ids?: string[];
  } | null;
  /**
   * The password itself is never here; it is read from .../demo-login on each View (SF-4713).
   * `password_available` is true once the demo company's shared password exists.
   */
  demo_login?: { email: string; password_available: boolean } | null;
  /**
   * SF-4770: every user the build made, demo login first. Absent from an older sf-api, and the
   * panel then shows only the demo login. Never carries a password.
   */
  demo_users?: DemoUser[];
  /** SF-4770: opens the web app in the demo company (sf-api builds it). Absent or null: no link. */
  web_app_link?: string | null;
  /**
   * SF-4848: one link per active survey the build made (Client Connect left out), each opening the
   * survey on the survey site at one of the demo's locations. Absent from an older sf-api: none.
   */
  survey_links?: DemoSurveyLink[];
  /** SF-4848: true when sf-api could not read the survey links (survey_links is then []). */
  survey_links_error?: boolean;
  /**
   * SF-4848: the web app sign-in page. It carries no email or password; both are copied from Demo login.
   * Absent or null: no link.
   */
  demo_sign_in_link?: string | null;
  approved_at?: string | null;
  finished_at?: string | null;
  attempt?: number;
}

/** SF-4848: a survey the build made, with its address on the survey site (sf-api builds it). */
export interface DemoSurveyLink {
  id: string;
  name: string;
  url: string;
}

/** SF-4770: one user the build made. `role` is the display word (Admin, Manager, Staff). */
export interface DemoUser {
  id: string;
  name: string;
  email: string;
  role: string;
  location_count: number;
  /** True for the user whose email is the demo login. */
  is_demo_login: boolean;
}

export interface DemoLogin {
  email: string | null;
  password: string;
}

/** Keep reading while the build is building; stop once it has finished (or failed). */
export const progressRefetchInterval = (build: DemoBuildRunView | undefined) =>
  build?.status === 'building' ? DEMO_PROGRESS_POLL_MS : false;

/**
 * One demo build, read again every few seconds while it is building (like the evaUsageClient
 * live hooks). Polling pauses while the tab is hidden and stops when the page closes (no observer).
 */
export function useDemoBuildProgress(id: string | undefined, enabled: boolean) {
  return useCustom<DemoBuildRunView>({
    url: `/${DEMO_BUILDS_BASE}/${encodeURIComponent(id ?? '')}`,
    method: 'get',
    queryOptions: {
      queryKey: demoBuildQueryKey(id),
      enabled: enabled && !!id,
      keepPreviousData: false,
      // Asked after every read with the cached build, so polling follows the cached status even
      // when a read fails.
      refetchInterval: (data) => progressRefetchInterval(data?.data),
      refetchIntervalInBackground: false,
    },
    errorNotification: false,
  });
}

export const resumeDemoBuildArgs = (buildId: string) => ({
  url: `/${DEMO_BUILDS_BASE}/${encodeURIComponent(buildId)}/resume`,
  method: 'post' as const,
  values: {},
  ...quiet,
});

/**
 * GET .../demo-login: the email and the demo company's shared password (SF-4713). Called only when
 * the user presses View, never on page load, and never cached.
 */
export const demoLoginUrl = (buildId: string) =>
  `/${DEMO_BUILDS_BASE}/${encodeURIComponent(buildId)}/demo-login`;
